/**
 * The review page's decision area for a refused photo (2026-10-19: the review page is the
 * ONE place to decide; it no longer sends the reviewer to another tab).
 *
 * Runs the four actions through refused-photos-api.ts and draws RefusedPhotoCard (pure).
 * After a decision the parent reloads the page's detail, so the card shows the new state;
 * a refusal that means "this row changed meanwhile" (already decided, expired, gone)
 * reloads it too, so the reviewer never acts on a stale state twice.
 */
import { useCallback, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import {
  confirmRefused,
  escalateRefused,
  restoreRefused,
  revealRefused,
  RefusedActionError,
  type QuarantineItem,
} from './refused-photos-api';
import { RefusedPhotoCard } from './RefusedPhotoCard';

/** Codes that mean the page shows a stale state: reload it. */
const STALE_CODES = new Set(['already_decided', 'quarantine_expired', 'quarantine_file_gone', 'quarantine_not_found']);

export function RefusedPhotoReview({
  item, onDecided, onOpenUser,
}: {
  item: QuarantineItem;
  /** A decision was made (message) or the state is stale (null): reload the detail. */
  onDecided: (message: string | null) => void;
  onOpenUser: (userId: string) => void;
}) {
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const [revealedUrl, setRevealedUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const permissions = {
    reveal: hasPermission('moderation.photos.reveal'),
    approve: hasPermission('moderation.photos.approve'),
    reject: hasPermission('moderation.photos.reject'),
    legalHold: hasPermission('moderation.photos.legal_hold'),
  };

  const run = useCallback(async (action: () => Promise<unknown>, done: string | null) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      if (done !== null) onDecided(done);
    } catch (e) {
      const err = e instanceof RefusedActionError ? e : new RefusedActionError(null, null, 'The request failed.');
      setError(err.message);
      if (err.code && STALE_CODES.has(err.code)) onDecided(null);
    } finally {
      setBusy(false);
    }
  }, [onDecided]);

  return (
    <RefusedPhotoCard
      item={item}
      permissions={permissions}
      revealedUrl={revealedUrl}
      busy={busy}
      error={error}
      onOpenUser={onOpenUser}
      onReveal={(reason) => void run(async () => {
        const r = await revealRefused(apiUrl, item.photo_id, reason);
        setRevealedUrl(r.url);
      }, null)}
      onRestore={(notes) => void run(() => restoreRefused(apiUrl, item.photo_id, notes),
        'Photo restored. It is back in the member\'s photos, and the member has been told.')}
      onConfirm={(notes) => void run(() => confirmRefused(apiUrl, item.photo_id, notes),
        'Refusal confirmed. The photo is kept, out of the member\'s photos.')}
      onEscalate={(notes) => void run(() => escalateRefused(apiUrl, item.photo_id, notes),
        'Photo put on legal hold. The owner has been alerted. Report it today.')}
    />
  );
}
